import { API_HTTP_ERROR_PREFIX, CR_AUTOMATION_API_BASE_URL, HTTP_METHODS, REQUEST_TIMEOUT, AUTH_TOKEN_KEY, AUTH_USER_KEY } from '../constants';

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = CR_AUTOMATION_API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT.DEFAULT_MS);

    const config: RequestInit = {
      method: options.method || HTTP_METHODS.GET,
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      body: options.body,
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`${API_HTTP_ERROR_PREFIX} ${response.status} ${response.statusText}`);
      }

      // Handle responses with no content (like DELETE requests)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T;
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      // For non-JSON responses or empty responses, return undefined
      return undefined as T;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error(`Request timed out after ${REQUEST_TIMEOUT.DEFAULT_MS / 1000}s: ${url}`);
      }
      console.error('API Request failed:', error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url = `${endpoint}?${searchParams.toString()}`;
    }
    
    return this.request<T>(url);
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: HTTP_METHODS.POST,
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: HTTP_METHODS.PUT,
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: HTTP_METHODS.PATCH,
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: HTTP_METHODS.DELETE,
    });
  }

  /**
   * Uploads a file via multipart/form-data POST request.
   * @param endpoint - The API endpoint path
   * @param file - The file to upload
   * @param fieldName - The form field name for the file (defaults to 'file')
   * @returns Parsed JSON response of type T
   * @throws Error if the request fails
   */
  async uploadFile<T>(endpoint: string, file: File, fieldName: string = 'file'): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      const response = await fetch(url, {
        method: HTTP_METHODS.POST,
        headers: this.getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorBody = await response.json();
          const message = Array.isArray(errorBody.message)
            ? errorBody.message.join('; ')
            : (errorBody.message ?? `${API_HTTP_ERROR_PREFIX} ${response.status}`);
          throw new Error(message);
        }
        throw new Error(`${API_HTTP_ERROR_PREFIX} ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return undefined as T;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  /**
   * Uploads a file with additional form fields via multipart/form-data POST request.
   * @param endpoint - The API endpoint path
   * @param file - The file to upload
   * @param fields - Additional string fields to include in the form data
   * @param fieldName - The form field name for the file (defaults to 'file')
   * @returns Parsed JSON response of type T
   * @throws Error if the request fails
   */
  async uploadFileWithFields<T>(
    endpoint: string,
    file: File,
    fields: Record<string, string> = {},
    fieldName: string = 'file',
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const formData = new FormData();
    formData.append(fieldName, file);

    for (const [key, value] of Object.entries(fields)) {
      formData.append(key, value);
    }

    try {
      const response = await fetch(url, {
        method: HTTP_METHODS.POST,
        headers: this.getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorBody = await response.json();
          const message = Array.isArray(errorBody.message)
            ? errorBody.message.join('; ')
            : (errorBody.message ?? `${API_HTTP_ERROR_PREFIX} ${response.status}`);
          throw new Error(message);
        }
        throw new Error(`${API_HTTP_ERROR_PREFIX} ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return undefined as T;
    } catch (error) {
      console.error('File upload with fields failed:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();